/**
 * JP Histogram — John Person's pivot-momentum histogram (the "PivotAvg" plot).
 * EXACT formula, reverse-engineered from a JP-histogram export (PivotAvg /
 * AvgLine / ZeroLine columns) and verified to 0.00 across all bars by
 * replicating the export's per-step rounding to the cent (the earlier ≈0.05
 * residual was exactly that un-replicated rounding):
 *
 *   P (pivot) = round( (H + L + C) / 3, 2 )           ( the prior bar's pivot )
 *   FastAvg   = round( EMA(Close, emaLen), 2 )         ( XAverage(Close, 5) )
 *   SlowAvg   = round( SMA(P, pivotLen), 2 )           ( = Σ of the last pivots / n )
 *   PivotAvg (hist) = round( pivotLen * (FastAvg − SlowAvg), 2 )
 *                   = pivotLen * EMA(Close, emaLen) − Σ_{k=1..pivotLen} P[i−k]
 *                   ( i.e. the sum, over the last `pivotLen` pivots, of how far
 *                     the smoothed close sits above each — defaults 5 / 5 )
 *   AvgLine  (avg)  = SMA(PivotAvg, sigLen)        ( default sigLen = 5 )
 *
 * The "~2.8x scale" mystery from earlier fits was actually this ×pivotLen (×5)
 * pivot construction. EMA = recursive (TradeStation XAverage), seeded at bar
 * pivotLen — NOT bar 0. TradeStation's XAverage seeds at CurrentBar = 1, which is
 * bar MaxBarsBack (= pivotLen for this study); matching that seed bar lines the
 * warmup up with the platform to the cent (verified vs AAPL/PLTR exports). The
 * converged region is identical to a bar-0 seed.
 *
 * WARMUP: blank the first emaLen + pivotLen bars. The close-EMA needs ~emaLen
 * bars to settle, and PivotAvg then needs pivotLen pivots from that settled EMA,
 * so the first PivotAvg lands on bar emaLen+pivotLen (bar 10 with the 5/5
 * defaults) — confirmed against a PLTR weekly chart whose "correct" histogram
 * starts exactly there (12/11/2020). Starting earlier plots un-warmed bars the
 * source does not. The few bars right after warmup can differ by a point or two
 * from the source's EMA initialization, converging to <0.05 by ~bar 20.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import { resampleOHLC, htIndexPerDay, type HtFreq } from "./weekly";
import type { OHLCV, IndicatorParams, SharedIndicatorDef, SharedParam } from "../types";

const num = (v: unknown, d: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : d;
export const jpLen = (v: unknown, d: number): number => Math.max(1, Math.round(num(v, d)));
/** Round to the cent — the export rounds Pivot / FastAvg / SlowAvg / PivotAvg at
 *  every step, so replicating it makes the histogram match to 0.00. */
const round2 = (x: number): number => Math.round(x * 100) / 100;

/** Simple moving average; NaN inputs count as 0 (matches the source's 0-padded
 *  warmup so AvgLine reproduces exactly). */
export function smaArr(x: number[], L: number): number[] {
  const n = x.length;
  const out = new Array<number>(n).fill(NaN);
  let s = 0;
  for (let i = 0; i < n; i++) {
    const v = Number.isFinite(x[i]!) ? x[i]! : 0;
    s += v;
    if (i >= L) s -= Number.isFinite(x[i - L]!) ? x[i - L]! : 0;
    if (i >= L - 1) out[i] = s / L;
  }
  return out;
}

/** EMA seeded recursively at the first finite value (TradeStation XAverage). */
export function emaArr(x: number[], L: number): number[] {
  const n = x.length;
  const a = 2 / (L + 1);
  const out = new Array<number>(n).fill(NaN);
  let prev = NaN;
  for (let i = 0; i < n; i++) {
    const v = x[i]!;
    if (!Number.isFinite(v)) { out[i] = prev; continue; }
    prev = Number.isFinite(prev) ? a * v + (1 - a) * prev : v;
    out[i] = prev;
  }
  return out;
}

/**
 * PivotAvg = pivotLen * EMA(Close, emaLen) − Σ_{k=1..pivotLen} hlc3[i−k].
 * NaN until index >= emaLen + pivotLen (the source's warmup — see file header).
 */
export function pivotHistogram(data: OHLCV, emaLen = 5, pivotLen = 5): number[] {
  const { highs, lows, closes } = data;
  const n = closes.length;
  if (!highs || !lows || n === 0) return new Array<number>(n).fill(NaN);
  // Pivot = the bar's typical price rounded to the cent (the export rounds it);
  // summed over the prior `pivotLen` bars this IS the SlowAvg numerator.
  const hlc3 = new Array<number>(n);
  for (let i = 0; i < n; i++) hlc3[i] = round2((highs[i]! + lows[i]! + closes[i]!) / 3);
  // Close-EMA seeded at bar pivotLen (TradeStation's XAverage seeds at CurrentBar
  // = 1 = bar MaxBarsBack = pivotLen), not bar 0 — so the warmup matches the
  // platform. NaN before the seed bar; converged region is unaffected.
  const a = 2 / (emaLen + 1);
  const ema = new Array<number>(n).fill(NaN);
  let prev = NaN;
  for (let i = pivotLen; i < n; i++) {
    prev = Number.isFinite(prev) ? a * closes[i]! + (1 - a) * prev : closes[i]!;
    ema[i] = prev;
  }
  const out = new Array<number>(n).fill(NaN);
  // Blank the warmup: pivots must exist and the EMA must have started — first
  // value lands on bar emaLen+pivotLen (matches the source's first plotted bar).
  for (let i = emaLen + pivotLen; i < n; i++) {
    const fast = round2(ema[i]!); // FastAvg = round(EMA(Close, emaLen), 2)
    let s = 0;
    for (let k = 1; k <= pivotLen; k++) s += hlc3[i - k]!;
    const slow = round2(s / pivotLen); // SlowAvg = round(SMA(Pivot, pivotLen), 2)
    out[i] = round2(pivotLen * (fast - slow)); // PivotAvg = round(pivotLen*(FastAvg − SlowAvg), 2)
  }
  return out;
}

export type JpHistParts = {
  hist: (number | null)[]; // PivotAvg
  avg: (number | null)[]; // AvgLine = SMA(PivotAvg, sigLen)
};

export function computeJpHistogram(data: OHLCV, params: IndicatorParams = {}): JpHistParts {
  const emaLen = jpLen(params.emaLen, 5);
  const pivotLen = jpLen(params.pivotLen, 5);
  const sigLen = jpLen(params.sigLen, 5);
  const raw = pivotHistogram(data, emaLen, pivotLen);
  const avgRaw = smaArr(raw, sigLen);
  // AvgLine is the 0-padded SMA of PivotAvg, so smaArr makes it finite a few bars
  // early (averaging the warmup zeros). Blank it until PivotAvg itself starts
  // (emaLen+pivotLen) so the chart shows no flat-zero stub before the histogram.
  const warmup = emaLen + pivotLen;
  const hist = raw.map((v) => (Number.isFinite(v) ? v : null));
  const avg = avgRaw.map((v, i) => (i >= warmup && Number.isFinite(v) ? v : null));
  return { hist, avg };
}

export const JP_HISTOGRAM_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "emaLen", label: "Close EMA", kind: "number", default: 5, min: 1, max: 100, step: 1 },
  { id: "pivotLen", label: "Pivot sum", kind: "number", default: 5, min: 1, max: 100, step: 1 },
  { id: "sigLen", label: "Signal SMA", kind: "number", default: 5, min: 1, max: 100, step: 1 },
];

/** Scanner / watchlist — the latest PivotAvg value (signed; + above zero). */
const histTfFreq = (tf: number | undefined): HtFreq | "day" =>
  tf === 2 ? "month" : tf === 1 ? "week" : "day";

/** Pivot-momentum histogram on the chosen timeframe, projected onto daily bars.
 *  Weekly/Monthly resample the HLC (pivotHistogram uses no volume) and project by
 *  period. No baked column — recomputes via the API. */
function histTfSeries(data: OHLCV, p: IndicatorParams, freq: HtFreq | "day"): (number | null)[] {
  if (freq === "day") return computeJpHistogram(data, p).hist;
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  const hist = computeJpHistogram({ highs: h.h, lows: h.l, closes: h.c } as OHLCV, p).hist;
  const idx = htIndexPerDay(times, h.t);
  return times.map((_, i) => { const k = idx[i]!; return k >= 0 && k < hist.length ? hist[k]! : null; });
}

export const JP_HISTOGRAM: SharedIndicatorDef = {
  id: "mpp-jp-histogram",
  label: "Afiat - Pivot Momentum",
  group: familyGroup("jphist"), // → "Pivot Studies"
  format: "signed",
  panelKey: "hist_d", // Daily; Weekly/Monthly via TF_PRECOMPUTED (hist_w / hist_m)
  params: [TF_DWM, ...JP_HISTOGRAM_PARAMS],
  computeSeries: (d, p) => histTfSeries(d, p, histTfFreq(p[TF_PARAM])),
};

export const JP_HISTOGRAM_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [JP_HISTOGRAM];
